<template>
    <div class="PopoverExample">
        <ComponentPlayground
            :component="Popover"
            :defaults="selfContainedDefaults"
            title="Popover — Self-contained"
            description="
                Manages open/close internally. #base slot exposes { toggle, isOpen }; #default exposes { close }.
            "
        >
            <template #default="{ liveProps }">
                <Popover v-bind="liveProps">
                    <template #base="{ toggle, isOpen }">
                        <button
                            class="PopoverExample__trigger"
                            :class="{ 'PopoverExample__trigger--open': isOpen }"
                            @click="toggle"
                        >
                            {{ isOpen ? 'Close popover' : 'Open popover' }}
                        </button>
                    </template>
                    <template #default="{ close }">
                        <div class="PopoverExample__content">
                            <p class="PopoverExample__contentText">
                                Popover content goes here. Click outside or below to close.
                            </p>
                            <button class="PopoverExample__closeBtn" @click="close">
                                Close
                            </button>
                        </div>
                    </template>
                </Popover>
            </template>
        </ComponentPlayground>

        <ComponentPlayground
            :component="Popover"
            :defaults="controlledDefaults"
            title="Popover — Controlled"
            description="Parent controls open state via :open. Emits @hide when user clicks outside."
        >
            <template #default="{ liveProps }">
                <Popover v-bind="liveProps" @hide="liveProps.open = false">
                    <template #base>
                        <button
                            class="PopoverExample__trigger"
                            :class="{ 'PopoverExample__trigger--open': liveProps.open }"
                            @click="liveProps.open = !liveProps.open"
                        >
                            {{ liveProps.open ? 'Close popover' : 'Open popover' }}
                        </button>
                    </template>
                    <template #default="{ close }">
                        <div class="PopoverExample__content">
                            <p class="PopoverExample__contentText">
                                Controlled popover. Click outside or below to close.
                            </p>
                            <button class="PopoverExample__closeBtn" @click="close">
                                Close
                            </button>
                        </div>
                    </template>
                </Popover>
            </template>
        </ComponentPlayground>
    </div>
</template>

<style lang="scss" scoped>
.PopoverExample {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    max-width: 56rem;

    &__trigger {
        padding: 0.5rem 1rem;
        font-size: 0.875rem;
        font-weight: 500;
        color: #fff;
        background: var(--eidSecureBlue);
        border: none;
        border-radius: 0.375rem;
        cursor: pointer;
        transition: background 0.15s;

        &:hover,
        &--open {
            background: var(--eidSecureBlue-75);
        }
    }

    &__content {
        min-width: 220px;
        padding: 1rem;
        background: #fff;
        border-radius: 0.5rem;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        border: 1px solid var(--eidTechGray-10);
    }

    &__contentText {
        margin: 0 0 0.75rem;
        font-size: 0.875rem;
        color: var(--eidTechGray);
    }

    &__closeBtn {
        padding: 0.375rem 0.75rem;
        font-size: 0.8125rem;
        font-weight: 500;
        color: var(--eidTechGray-75);
        background: var(--eidTechGray-10);
        border: none;
        border-radius: 0.25rem;
        cursor: pointer;

        &:hover {
            background: var(--eidTechGray-25);
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { Popover } from '@evidentid/dashboard-commons/components/Popover';

    import ComponentPlayground from './ComponentPlayground.vue';

    export default defineComponent({
        name: 'PopoverExample',
        components: { ComponentPlayground, Popover },
        data() {
            return {
                Popover,
                selfContainedDefaults: {
                    selfContained: true,
                    positionX: 'start' as 'start' | 'end',
                    positionY: 'bottom' as 'top' | 'bottom',
                    autoFlip: false,
                    offsetX: 0,
                    offsetY: 0,
                },
                controlledDefaults: {
                    open: false,
                    positionX: 'start' as 'start' | 'end',
                    positionY: 'bottom' as 'top' | 'bottom',
                    autoFlip: false,
                    offsetX: 0,
                    offsetY: 0,
                },
            };
        },
    });
</script>
